import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '../use-toast';
import { showErrorToast } from '../../utils/errorHandler';

/**
 * Custom hook to manage active ride data fetching and coordination
 */
export const useActiveRide = (initialRide) => {
  const [ride, setRide] = useState(initialRide || null);
  const [loading, setLoading] = useState(!initialRide);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [navigationInfo, setNavigationInfo] = useState(null);
  const watchIdRef = useRef(null);
  const postIntervalRef = useRef(null);
  const latestLocationRef = useRef(null);

  // Helper to extract lat/lng from various coordinate object formats
  const extractLatLng = useCallback((obj) => {
    if (!obj || typeof obj !== 'object') return null;
    const lat = obj.lat ?? obj.latitude;
    const lng = obj.lng ?? obj.longitude;
    if (lat != null && lng != null) return { lat: Number(lat), lng: Number(lng) };
    return null;
  }, []);

  // Extract coordinates from ride data immediately if available
  const extractStoredCoords = useCallback((rideData) => {
    if (!rideData) return;
    
    // Try all known pickup coordinate fields
    const pickupSources = [rideData.pickupCoords, rideData.pickupLocation, rideData.pickupCoordinates];
    for (const src of pickupSources) {
      const coords = extractLatLng(src);
      if (coords) { setPickupCoords(coords); break; }
    }
    
    // Try all known dropoff coordinate fields
    const dropoffSources = [rideData.dropoffCoords, rideData.dropoffLocation, rideData.dropoffCoordinates];
    for (const src of dropoffSources) {
      const coords = extractLatLng(src);
      if (coords) { setDropoffCoords(coords); break; }
    }
  }, [extractLatLng]);

  useEffect(() => {
    if (!ride) {
      fetchActiveRide();
    } else {
      // First extract stored coordinates immediately
      extractStoredCoords(ride);
      // Then fetch/geocode if needed
      fetchCoordinates(ride);
    }
    
    // Start continuous location tracking
    startLocationTracking();

    return () => stopLocationTracking();
  }, []);

  // Continuous GPS tracking with periodic backend posting
  const startLocationTracking = useCallback(() => {
    if (!navigator.geolocation) return;

    // Watch position for continuous updates
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        setDriverLocation(loc);
        latestLocationRef.current = loc;
      },
      (error) => {
        console.error('Error watching driver location:', error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 3000 }
    );

    // Post location to backend every 5 seconds
    const postLocation = async () => {
      const loc = latestLocationRef.current;
      if (!loc) return;
      try {
        const api = (await import('../../utils/api')).default;
        await api.put('/drivers/location', { latitude: loc.lat, longitude: loc.lng });
      } catch (e) {
        // Silent fail — rider may not be connected
      }
    };
    postIntervalRef.current = setInterval(postLocation, 5000);
    // Send immediately once
    postLocation();
  }, []);

  const stopLocationTracking = useCallback(() => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (postIntervalRef.current) {
      clearInterval(postIntervalRef.current);
      postIntervalRef.current = null;
    }
  }, []);

  const fetchActiveRide = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      
      // Check for accepted, arriving, and in_progress rides
      const acceptedResponse = await api.get('/drivers/my-rides?status=accepted');
      const arrivingResponse = await api.get('/drivers/my-rides?status=arriving');
      const inProgressResponse = await api.get('/drivers/my-rides?status=in_progress');
      
      const acceptedRides = acceptedResponse.data.rides || [];
      const arrivingRides = arrivingResponse.data.rides || [];
      const inProgressRides = inProgressResponse.data.rides || [];
      
      // Prioritize in_progress, then arriving, then accepted
      const activeRide = inProgressRides[0] || arrivingRides[0] || acceptedRides[0];
      
      if (activeRide) {
        setRide(activeRide);
        setLoading(false);
        
        // Extract stored coordinates immediately for faster map display
        extractStoredCoords(activeRide);
        
        // Then fetch/geocode any missing coordinates
        await fetchCoordinates(activeRide);
      } else {
        toast({
          title: 'No Active Ride',
          description: 'You do not have any active rides',
          variant: 'destructive',
        });
        return { noActiveRide: true };
      }
    } catch (error) {
      console.error('Error fetching active ride:', error);
      showErrorToast(toast, error, 'Failed to load active ride');
      setLoading(false);
    }
  };

  const fetchCoordinates = async (rideData = ride) => {
    if (!rideData) return;

    try {
      // Try all known coordinate fields for pickup
      const pickupSources = [rideData.pickupCoords, rideData.pickupLocation, rideData.pickupCoordinates];
      let pickupLatLng = null;
      for (const src of pickupSources) {
        const c = extractLatLng(src);
        if (c) { pickupLatLng = c; break; }
      }

      // Try all known coordinate fields for dropoff
      const dropoffSources = [rideData.dropoffCoords, rideData.dropoffLocation, rideData.dropoffCoordinates];
      let dropoffLatLng = null;
      for (const src of dropoffSources) {
        const c = extractLatLng(src);
        if (c) { dropoffLatLng = c; break; }
      }

      const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!googleMapsApiKey) {
        console.error('REACT_APP_GOOGLE_MAPS_API_KEY not configured');
        return;
      }
      
      // Only geocode if we don't have stored coordinates
      if (!pickupLatLng && rideData.pickup) {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(rideData.pickup)}&region=zm&key=${googleMapsApiKey}`
        );
        const data = await res.json();
        if (data.results?.[0]) {
          const loc = data.results[0].geometry.location;
          pickupLatLng = { lat: loc.lat, lng: loc.lng };
        }
      }

      if (!dropoffLatLng && rideData.dropoff) {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(rideData.dropoff)}&region=zm&key=${googleMapsApiKey}`
        );
        const data = await res.json();
        if (data.results?.[0]) {
          const loc = data.results[0].geometry.location;
          dropoffLatLng = { lat: loc.lat, lng: loc.lng };
        }
      }

      if (pickupLatLng && dropoffLatLng) {
        setPickupCoords(pickupLatLng);
        setDropoffCoords(dropoffLatLng);
      }
    } catch (error) {
      console.error('Error fetching coordinates:', error);
    }
  };

  const handleRouteUpdate = (routeInfo) => {
    setNavigationInfo(routeInfo);
  };

  // Determine navigation origin and destination based on ride status
  const getNavigationPoints = () => {
    if (!ride) return { origin: null, destination: null, label: '' };
    
    const status = ride.status;
    
    // Before picking up customer: navigate from driver location to pickup
    if (status === 'accepted' || status === 'arriving' || status === 'arrived') {
      return {
        origin: driverLocation || pickupCoords, // Fallback to pickup if no driver location
        destination: pickupCoords,
        originLabel: 'Your Location',
        destinationLabel: 'Pickup Point',
        navLabel: 'Navigate to Pickup'
      };
    }
    
    // After picking up customer: navigate from pickup to dropoff
    return {
      origin: pickupCoords,
      destination: dropoffCoords,
      originLabel: 'Pickup',
      destinationLabel: 'Drop-off',
      navLabel: 'Navigate to Drop-off'
    };
  };

  return {
    ride,
    setRide,
    loading,
    pickupCoords,
    dropoffCoords,
    driverLocation,
    navigationInfo,
    fetchActiveRide,
    handleRouteUpdate,
    getNavigationPoints,
    refreshDriverLocation: startLocationTracking,
  };
};
